import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Gavel, Plus, Loader2, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Urgency = 'low' | 'medium' | 'high' | 'urgent';
type AdvisoryStatus = 'pending' | 'in_review' | 'answered' | 'closed';

interface LegalAdvisory {
  id: string;
  title: string;
  question: string;
  context: string;
  urgency: Urgency;
  status: AdvisoryStatus;
  answer?: string;
  created_at: string;
  updated_at: string;
}

interface LegalResponse {
  advisories: LegalAdvisory[];
  stats: {
    total: number;
    pending: number;
    answered: number;
  };
}

interface CreateAdvisoryPayload {
  title: string;
  question: string;
  context: string;
  urgency: Urgency;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function urgencyBadge(urgency: Urgency) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (urgency) {
    case 'urgent':
      return <span className={`${base} bg-red-500/15 text-red-500`}>Urgent</span>;
    case 'high':
      return <span className={`${base} bg-orange-500/15 text-orange-500`}>High</span>;
    case 'medium':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Medium</span>;
    case 'low':
      return <span className={`${base} bg-blue-500/15 text-blue-500`}>Low</span>;
  }
}

function statusBadge(status: AdvisoryStatus) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (status) {
    case 'pending':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Pending</span>;
    case 'in_review':
      return <span className={`${base} bg-blue-500/15 text-blue-500`}>In Review</span>;
    case 'answered':
      return <span className={`${base} bg-green-500/15 text-green-500`}>Answered</span>;
    case 'closed':
      return <span className={`${base} bg-muted text-muted-foreground`}>Closed</span>;
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function LegalAdvisoryPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateAdvisoryPayload>({
    title: '',
    question: '',
    context: '',
    urgency: 'medium',
  });

  const legalQuery = useQuery<LegalResponse>({
    queryKey: ['governance', 'legal'],
    queryFn: () => api<LegalResponse>('governance/legal'),
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateAdvisoryPayload) =>
      api('governance/legal', { body: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governance', 'legal'] });
      setShowForm(false);
      setForm({ title: '', question: '', context: '', urgency: 'medium' });
    },
  });

  const stats = legalQuery.data?.stats;
  const advisories = legalQuery.data?.advisories ?? [];

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim() || !form.question.trim()) return;
    createMutation.mutate(form);
  }

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Gavel className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Legal AI Advisory</h1>
            <p className="text-muted-foreground mt-1">
              Submit legal questions about AI usage and track advisory responses.
            </p>
          </div>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 flex-shrink-0"
          >
            <Plus className="h-4 w-4" />
            New Request
          </button>
        )}
      </div>

      {/* Stats */}
      {legalQuery.isLoading ? (
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Total Requests</p>
            <p className="text-2xl font-bold text-foreground">{stats?.total ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Pending</p>
            <p className="text-2xl font-bold text-yellow-500">{stats?.pending ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Answered</p>
            <p className="text-2xl font-bold text-green-500">{stats?.answered ?? 0}</p>
          </div>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-card p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">New Advisory Request</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Title</label>
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Brief title for your question"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Urgency</label>
              <select
                value={form.urgency}
                onChange={(e) => setForm({ ...form, urgency: e.target.value as Urgency })}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Question</label>
            <textarea
              required
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              rows={3}
              placeholder="What is your legal question regarding AI usage?"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-y"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Context</label>
            <textarea
              value={form.context}
              onChange={(e) => setForm({ ...form, context: e.target.value })}
              rows={2}
              placeholder="Provide additional context — affected systems, regulations, jurisdictions..."
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-y"
            />
          </div>

          {createMutation.isError && (
            <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-500">
              {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to submit request.'}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={createMutation.isPending || !form.title.trim() || !form.question.trim()}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {createMutation.isPending ? 'Submitting...' : 'Submit Request'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Requests list */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">Advisory Requests</h2>

        {legalQuery.isLoading && (
          <div className="rounded-lg border border-border bg-card p-12 flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Loading requests...</span>
          </div>
        )}

        {legalQuery.isSuccess && advisories.length === 0 && (
          <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground">
            No advisory requests yet. Submit a question to get legal guidance on AI usage.
          </div>
        )}

        {legalQuery.isSuccess && advisories.length > 0 && (
          <div className="space-y-3">
            {advisories.map((adv) => (
              <div key={adv.id} className="rounded-lg border border-border bg-card overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedId(expandedId === adv.id ? null : adv.id)}
                  className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{adv.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{adv.question}</p>
                  </div>
                  <span className="flex-shrink-0">{urgencyBadge(adv.urgency)}</span>
                  <span className="flex-shrink-0">{statusBadge(adv.status)}</span>
                  <span className="text-sm text-muted-foreground flex-shrink-0 w-24">{formatDate(adv.created_at)}</span>
                </button>
                {expandedId === adv.id && (
                  <div className="px-5 pb-4 border-t border-border pt-3 space-y-3">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase mb-1">Question</p>
                      <p className="text-sm text-foreground">{adv.question}</p>
                    </div>
                    {adv.context && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase mb-1">Context</p>
                        <p className="text-sm text-foreground">{adv.context}</p>
                      </div>
                    )}
                    {adv.answer && (
                      <div className="rounded-md bg-accent/50 p-4">
                        <p className="text-xs font-medium text-foreground uppercase mb-1">Advisory Response</p>
                        <p className="text-sm text-foreground">{adv.answer}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
